import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';

import { InteractionExampleCard } from '@/components/public/InteractionExampleCard';
import { motion } from '@/components/public/PublicMotion';
import { PenUnderline } from '@/components/public/PenUnderline';
import { PublicDemoLink } from '@/components/public/PublicDemoLink';
import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicSeo } from '@/components/public/PublicSeo';
import { SectionEyebrow } from '@/components/public/SectionEyebrow';
import { WeeklySummaryCard } from '@/components/public/WeeklySummaryCard';
import { Button } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import {
  fadeInUp,
  revealTransition,
  scaleIn,
  sectionReveal,
  staggerContainer,
} from '@/hooks/use-scroll-animation';

// ─── Contenuti ──────────────────────────────────────────────────────────────

const stepPills = [
  { id: 'step1', label: '01 · Crei il profilo' },
  { id: 'step2', label: '02 · La persona annota' },
  { id: 'step3', label: '03 · L’IA mette a fuoco' },
  { id: 'step4', label: '04 · Ricevi il materiale' },
  { id: 'step5', label: '05 · Lavorate insieme' },
] as const;

const steps = {
  step1: {
    number: '01',
    audience: 'Lo psicologo',
    title: 'Decidi tu chi entra e come l’IA si comporta.',
    body: 'Crei il profilo del cliente e gli assegni una licenza. Nel profilo definisci come l’IA deve rivolgersi a quella persona: caratterizzazione, eventuali neurodivergenze, condizioni rilevanti, istruzioni specifiche.',
    bullets: [
      'Pochi secondi per attivare ogni cliente',
      'Istruzioni e condizioni definite da te, vincolanti per l’IA',
      'Da «la nostra IA per te» a «la tua IA»',
    ],
  },
  step2: {
    number: '02',
    audience: 'La persona',
    title: 'Annota quando accade, non quando se lo ricorda.',
    body: 'Quando succede qualcosa di rilevante — un episodio, un’emozione forte, un pensiero che torna — apre l’app e racconta. Chat e diario sono formati che conosce già: non c’è niente da imparare.',
    bullets: [
      'Chat disponibile a qualsiasi ora, quando serve',
      'Diario libero, o guidato per chi resta davanti alla pagina bianca',
      'Pochi minuti, nessun compito, nessuna scadenza',
    ],
  },
  step3: {
    number: '03',
    audience: 'L’IA Interrogativa',
    title: 'Mette a fuoco con domande. Solo domande.',
    body: 'Risponde a ciò che la persona scrive con domande che chiariscono episodio, contesto, emozione e segnali — tarate sul profilo che hai definito tu. Non consola, non consiglia, non interpreta.',
    bullets: [
      'Domande legate a ciò che la persona scrive, una alla volta',
      'Nessuna diagnosi, nessuna indicazione terapeutica',
      'Ogni interazione è supervisionata dal sistema di sicurezza',
    ],
  },
  step4: {
    number: '04',
    audience: 'Lo psicologo',
    title: 'Ricevi materiale già in ordine, non pagine da setacciare.',
    body: 'Prima della seduta apri la dashboard e scegli il periodo che ti interessa. Emovia ha già rielaborato i contenuti e aggiornato, volta dopo volta, la scheda clinica.',
    bullets: [
      'Un riassunto del periodo, pronto da leggere',
      'Temi ricorrenti e pattern che da soli richiederebbero mesi per emergere',
      'Eventi chiave, con chat e diari originali sempre a portata di mano',
    ],
  },
} as const;

const step5Highlights = [
  'Sedute più mirate',
  'Continuità che sostiene l’alleanza',
  'Nessun cambio di metodo',
] as const;

const summaryThemes = ['Timore del giudizio', 'Sonno irregolare'] as const;

// ─── Elementi grafici a tratto di penna ─────────────────────────────────────

const PillConnector = () => (
  <svg viewBox="0 0 44 16" className="hidden h-4 w-11 text-gray-dark sm:block" aria-hidden>
    <path
      d="M4 9 C 14 4, 26 13, 40 8"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeDasharray="2 6"
    />
  </svg>
);

// Connettore verticale tratteggiato tra uno step e il successivo.
const StepConnector = ({ flip = false }: { flip?: boolean }) => (
  <div className="flex justify-center" aria-hidden>
    <svg viewBox="0 0 60 80" className="h-[62px] w-[46px] text-gray-dark">
      <path
        d={flip ? 'M30 4 C 46 24, 14 44, 30 72' : 'M30 4 C 14 24, 46 44, 30 72'}
        fill="none"
        stroke="currentColor"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeDasharray="2 8"
      />
    </svg>
  </div>
);

const PenStrokeIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <path d="M38 8 L14 32 L10 40 L18 36 L40 14 C 42 12 42 10 40 8 C 39 7 38.5 7.5 38 8 Z" />
    <path d="M8 42 C 14 40, 20 41, 26 42" opacity={0.5} />
  </svg>
);

// ─── Blocchi ricorrenti ─────────────────────────────────────────────────────

const StepBadge = ({ number, audience }: { number: string; audience: string }) => (
  <div className="flex items-center gap-3.5">
    <span className="flex h-13 w-13 items-center justify-center rounded-full bg-primary font-accent font-bold text-primary-foreground">
      {number}
    </span>
    <SectionEyebrow className="text-primary">{audience}</SectionEyebrow>
  </div>
);

const StepText = ({ step }: { step: (typeof steps)[keyof typeof steps] }) => (
  <div className="grid gap-4.5">
    <StepBadge number={step.number} audience={step.audience} />
    <Heading level={2} className="text-3xl font-bold text-primary-dark md:text-4xl">
      {step.title}
    </Heading>
    <p className="max-w-none text-lg text-gray-darker">{step.body}</p>
    <div className="grid gap-3">
      {step.bullets.map((bullet) => (
        <div key={bullet} className="flex gap-3">
          <span aria-hidden className="font-bold text-primary">
            —
          </span>
          {bullet}
        </div>
      ))}
    </div>
  </div>
);

const MockupReveal = ({ children, className }: { children: ReactNode; className?: string }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
    variants={scaleIn}
    transition={{ duration: 0.5 }}
    className={className}
  >
    {children}
  </motion.div>
);

// ─── Pagina ─────────────────────────────────────────────────────────────────

const ComeFunziona = () => {
  return (
    <PublicLayout>
      <PublicSeo
        title="Emovia | Come Funziona"
        description="Scopri come Emovia trasforma diari e conversazioni tra le sedute in materiale clinico organizzato e supervisionato dal professionista."
        path="/come-funziona"
      />

      {/* Hero — il ciclo in sintesi */}
      <section id="processo" className="border-b border-border bg-background">
        <div className="container max-w-[1160px] py-20 text-center md:py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="grid justify-items-center gap-7"
          >
            <motion.div variants={fadeInUp} transition={revealTransition}>
              <SectionEyebrow className="mb-5 text-primary" align="center">
                Il processo
              </SectionEyebrow>
              <Heading
                level={1}
                font="display"
                className="mx-auto max-w-[18ch] text-4xl text-primary-dark md:text-5xl"
              >
                Un ciclo che parte da te e torna <PenUnderline>da te.</PenUnderline>
              </Heading>
            </motion.div>

            <motion.p
              variants={fadeInUp}
              transition={revealTransition}
              className="max-w-[58ch] text-lg text-gray-darker md:text-xl"
            >
              Emovia non è un’app fai-da-te: la attivi tu, la regoli tu, e il materiale torna a te.
              In mezzo, la persona ha uno spazio guidato per fissare ciò che vive tra le sedute.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              transition={revealTransition}
              className="mt-4 flex flex-wrap items-center justify-center gap-y-3"
            >
              {stepPills.map((pill, index) => (
                <span key={pill.id} className="flex items-center">
                  {index > 0 ? <PillConnector /> : null}
                  <a
                    href={`#${pill.id}`}
                    className="rounded-full border border-blue-accent bg-blue-lighter px-4 py-2 text-sm font-semibold text-primary-dark no-underline"
                  >
                    {pill.label}
                  </a>
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Step 01 — Attivi e calibri */}
      <section id="step1" className="bg-background">
        <div className="container grid max-w-[1160px] gap-12 py-16 md:py-20 lg:grid-cols-2 lg:items-center lg:gap-16">
          <motion.div {...sectionReveal}>
            <StepText step={steps.step1} />
          </motion.div>

          <MockupReveal className="overflow-hidden rounded-3xl border border-border bg-card shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-6 py-4">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-light text-xs font-bold text-primary-dark"
                >
                  GR
                </span>
                <p className="font-bold text-primary-dark">Profilo cliente · Giulia R.</p>
              </div>
              <span className="rounded-full bg-green-light px-3 py-1 text-xs font-bold text-green-dark">
                Licenza attiva
              </span>
            </div>
            <div className="grid gap-4.5 p-6">
              <div>
                <p className="mb-2 font-accent text-xs uppercase tracking-wide text-muted-foreground">
                  Caratterizzazione
                </p>
                <p className="rounded border border-input px-3.5 py-3 text-sm text-gray-darker">
                  Tende a minimizzare gli episodi. Tono accogliente, mai esortativo.
                </p>
              </div>
              <div>
                <p className="mb-2 font-accent text-xs uppercase tracking-wide text-muted-foreground">
                  Condizioni rilevanti
                </p>
                <div className="flex gap-2">
                  <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-orange-dark">
                    Ansia sociale
                  </span>
                  <span className="rounded-full border border-dashed border-gray-dark px-3 py-1 text-xs font-semibold text-muted-foreground">
                    Aggiungi
                  </span>
                </div>
              </div>
              <div>
                <p className="mb-2 font-accent text-xs uppercase tracking-wide text-muted-foreground">
                  Istruzioni per l’IA
                </p>
                <p className="rounded border border-input px-3.5 py-3 text-sm text-gray-darker">
                  Domande brevi, una alla volta. Non proporre tecniche o esercizi.
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Esempio illustrativo con dati non reali.
              </p>
            </div>
          </MockupReveal>
        </div>
      </section>

      <StepConnector />

      {/* Step 02 — La persona annota */}
      <section id="step2" className="bg-background">
        <div className="container grid max-w-[1160px] gap-12 py-12 md:py-14 lg:grid-cols-2 lg:items-center lg:gap-16">
          <MockupReveal className="flex justify-center lg:order-1">
            <div className="w-[340px] max-w-full overflow-hidden rounded-[28px] border border-border bg-card shadow-card">
              <div className="flex items-center justify-between px-5 pt-4.5">
                <p className="font-bold text-primary-dark">Il tuo spazio</p>
                <span className="text-xs text-muted-foreground">lun · 22:14</span>
              </div>
              <div className="flex gap-2 px-5 pt-3.5">
                <span className="rounded-full bg-primary px-3.5 py-1.5 text-xs font-semibold text-primary-foreground">
                  Diario
                </span>
                <span className="rounded-full border border-border px-3.5 py-1.5 text-xs font-semibold text-gray-darker">
                  Chat
                </span>
              </div>
              <div className="grid gap-3 p-5">
                <p className="rounded-2xl bg-secondary px-4 py-3.5 text-[0.92rem] leading-relaxed text-secondary-foreground">
                  «Stanotte ho dormito poco, continuavo a pensare alla riunione di domani.»
                </p>
                <p className="rounded-2xl border border-dashed border-gray-dark px-4 py-3 text-sm text-muted-foreground">
                  Continua a scrivere, o lasciati guidare…
                </p>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Visibile al tuo psicologo: sì</span>
                  <span className="font-semibold text-primary">Modifica</span>
                </div>
              </div>
            </div>
          </MockupReveal>

          <motion.div {...sectionReveal} className="lg:order-2">
            <StepText step={steps.step2} />
          </motion.div>
        </div>
      </section>

      <StepConnector flip />

      {/* Step 03 — L'IA mette a fuoco */}
      <section id="step3" className="bg-background">
        <div className="container max-w-[1160px] py-12 md:py-14">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
            <motion.div {...sectionReveal}>
              <StepText step={steps.step3} />
            </motion.div>

            <MockupReveal className="rounded-3xl border border-border bg-card shadow-card">
              <InteractionExampleCard />
            </MockupReveal>
          </div>

          {/* In pratica */}
          <motion.div
            {...sectionReveal}
            className="mt-10 grid gap-6 rounded-3xl bg-secondary px-7 py-8 sm:grid-cols-[auto_1fr] md:px-9"
          >
            <PenStrokeIcon className="h-10 w-10 text-orange" />
            <div>
              <SectionEyebrow className="mb-2 text-orange-dark">In pratica</SectionEyebrow>
              <p className="max-w-[80ch] text-lg text-secondary-foreground">
                Giulia esce da una riunione andata male. La seduta è tra cinque giorni e sa che per
                allora ricorderà solo «una settimana pesante». Apre Emovia e scrive due righe; l’IA
                le chiede cosa ha provato esattamente — mette a fuoco che non era rabbia, era
                vergogna. In tre minuti l’episodio è fissato: contesto, emozione, comportamento.
                Giovedì la sua psicologa lo avrà già letto, e partiranno da lì.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <StepConnector />

      {/* Step 04 — Ricevi il materiale */}
      <section id="step4" className="bg-background">
        <div className="container grid max-w-[1160px] gap-12 py-12 md:py-14 lg:grid-cols-2 lg:items-center lg:gap-16">
          <MockupReveal className="overflow-hidden rounded-3xl border border-border bg-card shadow-card lg:order-1">
            <WeeklySummaryCard themes={summaryThemes} />
          </MockupReveal>

          <motion.div {...sectionReveal} className="lg:order-2">
            <StepText step={steps.step4} />
          </motion.div>
        </div>
      </section>

      <StepConnector flip />

      {/* Step 05 — Lavorate insieme */}
      <section id="step5" className="bg-background">
        <div className="container max-w-[880px] py-12 pb-24 text-center md:py-14 md:pb-28">
          <motion.div {...sectionReveal} className="grid justify-items-center gap-7">
            <StepBadge number="05" audience="In seduta" />
            <Heading level={2} className="text-3xl font-bold text-primary-dark md:text-4xl">
              La seduta parte da elementi concreti.
            </Heading>

            <div className="my-2 flex flex-wrap items-center justify-center gap-7">
              <p className="text-xl italic text-muted-foreground line-through decoration-gray-dark">
                «Com’è andata, cosa è successo?»
              </p>
              <svg viewBox="0 0 70 24" className="h-5 w-[58px] text-orange" aria-hidden>
                <path
                  d="M4 13 C 20 7, 40 17, 60 11"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={3}
                  strokeLinecap="round"
                />
                <path
                  d="M52 5 L62 11 L52 17"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="text-2xl font-bold text-primary-dark">«Perché è successo questo?»</p>
            </div>

            <p className="max-w-[60ch] text-lg text-gray-darker">
              I 15–20 minuti di ricostruzione tornano a essere tempo clinico. Niente riassunto
              iniziale: si lavora subito su pattern, significato, processo — con il tuo metodo e il
              tuo orientamento.
            </p>

            <div className="mt-2 flex flex-wrap justify-center gap-7 text-[0.95rem] text-gray-darker">
              {step5Highlights.map((highlight) => (
                <span key={highlight} className="flex items-center gap-2">
                  <span aria-hidden className="h-2 w-2 rounded-full bg-primary" />
                  {highlight}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA demo */}
      <section className="bg-primary-dark">
        <div className="container max-w-[880px] py-20 text-center md:py-24">
          <motion.div {...sectionReveal} className="grid justify-items-center gap-5">
            <Heading level={2} className="max-w-[24ch] text-3xl font-bold text-white md:text-4xl">
              Vuoi vederlo con i tuoi occhi?
            </Heading>
            <p className="max-w-[52ch] text-lg text-blue-light">
              Una dimostrazione concreta: il materiale che ricevi, come ragiona l’IA Interrogativa,
              dove si ferma. Senza giri di parole commerciali.
            </p>
            <Button
              size="lg"
              asChild
              className="group mt-2 h-13 bg-white px-7 text-base font-bold text-primary hover:bg-white/90"
            >
              <PublicDemoLink location="come_funziona_primary_cta">
                Richiedi una demo
                <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
              </PublicDemoLink>
            </Button>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ComeFunziona;
